import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BackendConfiguration} from '@application/configuration/backend-configuration';
import {ProductConfiguration} from '@domain/product-configuration/product-configuration';
import {SelectListProductConfiguration} from '@domain/product-configuration/select-list-product-configuration';
import {PropertyValue} from '@domain/property-value';
import {PlaceholderCategory} from '@domain/textile-data/finishing-and-finishing-template/placeholder-category';
import {OverviewListProductConfiguration} from '@presentation/pages/texedit/product-configuration/overview/overview-list-product-configuration';
import {GridModel} from '@vdw/angular-component-library';
import {Observable} from 'rxjs';
import {map as rxjsMap} from 'rxjs/operators';
import {ProductConfigurations} from './product-configuration.interface';

@Injectable()
export class HttpProductConfigurationsService implements ProductConfigurations {
  private httpClient: HttpClient;
  private backendConfiguration: BackendConfiguration;

  public constructor(httpClient: HttpClient, backendConfiguration: BackendConfiguration) {
    this.httpClient = httpClient;
    this.backendConfiguration = backendConfiguration;
  }

  public getAll(): Observable<OverviewListProductConfiguration[]> {
    return this.httpClient.get(`${this.backendConfiguration.getEndpoint()}product-configurations`).pipe(
      rxjsMap((productionConfigurationsJSON: any) => {
        return productionConfigurationsJSON.map((productionConfigurationJSON: any) => {
          return OverviewListProductConfiguration.fromJSON(productionConfigurationJSON);
        });
      })
    );
  }

  public getById(id: number): Observable<ProductConfiguration> {
    return this.httpClient
      .get(`${this.backendConfiguration.getEndpoint()}product-configurations/${id}`)
      .pipe(rxjsMap((productConfigurationJSON: any) => ProductConfiguration.fromJSON(productConfigurationJSON)));
  }

  public save(productionConfiguration: ProductConfiguration): Observable<number> {
    return this.httpClient.post(`${this.backendConfiguration.getEndpoint()}product-configurations`, productionConfiguration.toJSON()).pipe(rxjsMap((response: {id: number}) => response.id));
  }

  public update(productionConfiguration: ProductConfiguration): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getEndpoint()}product-configurations/${productionConfiguration.id}`, productionConfiguration.toJSON());
  }

  public delete(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.backendConfiguration.getEndpoint()}product-configurations/${id}`);
  }

  public hasAlreadyAdjustedCustomSettings(): Observable<boolean> {
    return this.httpClient
      .get(`${this.backendConfiguration.getEndpoint()}product-configurations/custom-settings/already-adjusted`)
      .pipe(rxjsMap((response: {alreadyAdjustedCustomSettings: boolean}) => response.alreadyAdjustedCustomSettings));
  }

  public getListOfCustomSettings(): Observable<PropertyValue[]> {
    return this.httpClient.get(`${this.backendConfiguration.getEndpoint()}product-configurations/custom-settings`).pipe(rxjsMap((response: PropertyValue[]) => response));
  }

  public saveCustomSettings(chosenSettings: PropertyValue[]): Observable<void> {
    const settingsToSave: PropertyValue[] = chosenSettings.map((chosenSetting: PropertyValue) => ({propertyName: chosenSetting.propertyName, propertyValue: chosenSetting.propertyValue}));

    return this.httpClient.put<void>(`${this.backendConfiguration.getEndpoint()}product-configurations/custom-settings`, settingsToSave);
  }

  public getCategoriesWithComponentsForProductConfigurationName(): Observable<PlaceholderCategory[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getEndpoint()}product-configurations/custom-settings/categories-with-components-for-product-configuration-name`)
      .pipe(rxjsMap((placeholderCategoriesJSON: any) => placeholderCategoriesJSON.map((placeholderCategoryJSON: any) => PlaceholderCategory.fromJSON(placeholderCategoryJSON))));
  }

  public getSlice(gridModel: GridModel): Observable<OverviewListProductConfiguration[]> {
    const url = `${this.backendConfiguration.getEndpoint()}product-configurations/get`;

    return this.httpClient.post(url, gridModel).pipe(
      rxjsMap((productionConfigurationsJSON: any) => {
        return productionConfigurationsJSON.map((productionConfiguration: any) => {
          return OverviewListProductConfiguration.fromJSON(productionConfiguration);
        });
      })
    );
  }

  public updateLocked(id: number, locked: boolean): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getEndpoint()}product-configurations/change-lock/${id}/${locked}`, null);
  }

  public updateReadyForProduction(id: number, version: number): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getEndpoint()}product-configurations/change-production-ready/${id}/${version}`, null);
  }

  public updateAdditionalInformation(id: number, version: number, additionalInformation: string): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getEndpoint()}product-configurations/${id}/${version}/additional-information`, JSON.stringify(additionalInformation));
  }

  public getPossibleProductConfigurationsForMachine(machineId: number): Observable<SelectListProductConfiguration[]> {
    return this.httpClient.get(`${this.backendConfiguration.getEndpoint()}product-configurations/machine/${machineId}/possible`).pipe(
      rxjsMap((productionConfigurationsJSON: any) => {
        return productionConfigurationsJSON.map(SelectListProductConfiguration.fromJSON);
      })
    );
  }

  public getPossibleValues(columnIdentifier: string): Observable<string[]> {
    return this.httpClient.get(`${this.backendConfiguration.getEndpoint()}product-configurations/${columnIdentifier}/possible-values`).pipe(rxjsMap((response: string[]) => response));
  }

  public getGeneratedProductConfigurationName(machineId: number, qualityId: number): Observable<string> {
    return this.httpClient
      .get(`${this.backendConfiguration.getEndpoint()}product-configurations/custom-settings/generate-name?machineId=${machineId}&machineQualityId=${qualityId}`)
      .pipe(rxjsMap((response: string) => response));
  }

  public isIdentifierAvailable(name: string): Observable<boolean> {
    const params = new HttpParams().set('name', name);

    return this.httpClient.get(`${this.backendConfiguration.getEndpoint(2)}product-configurations/check-name`, {params}).pipe(rxjsMap((response: {isAvailable: boolean}) => response.isAvailable));
  }

  public getNameGenerationProperties(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${this.backendConfiguration.getEndpoint()}product-configurations/custom-settings/name-generation-properties`);
  }

  public generateName(item: ProductConfiguration): Observable<string> {
    return this.httpClient.post<string>(`${this.backendConfiguration.getEndpoint()}product-configurations/custom-settings/generate-name`, item.toJSON());
  }
}
